import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PublisherCrmDrawer,
  PublisherCrmBadgeButton,
  PublisherCrmSlideOver,
  PUBLISHER_STYLES,
  INTRO_MEETING_OPTIONS,
  COMMUNICATION_CATEGORIES,
  loadAllCommunications,
  saveAllCommunications,
  getCommunicationsForUser,
  formatDateTimeTr,
} from "../components/PublisherCrmModule";

// API Endpoints
const API_LIST = "/api/scout-candidate-list.php";
const API_SAVE = "/api/scout-candidate-save.php";
const API_STATUS_UPDATE = "/api/scout-candidate-status-update.php";
const API_DELETE = "/api/scout-candidate-delete.php";
const API_USER_ADD = "/api/user-add.php";
const API_USERS_LIST = "/api/users-list.php";
const API_TIKTOK_PROFILE = "/api/tiktok-profile-fetch.php";
const API_AGENCY_STATUS_SAVE = "/api/scout-agency-status-save.php";
const API_AGENCY_STATUS_GET = "/api/scout-agency-status-get.php";

const STORAGE_KEY_SCOUT = "wolf_scout_candidates_v5";
const STORAGE_KEY_THEME = "wolf_admin_theme_mode";
const STORAGE_KEY_AGENCY_CACHE = "wolf_scout_agency_verified_cache_v1";

// Bilinen ve Doğrulanmış Backstage Ajans Kayıtları
const KNOWN_BACKSTAGE_AGENCY_MAP = {
  "userrss_723": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "estebiilgic": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "wua.pm": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "wuapm": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "c4.adem": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "_gulusm.2": { eligibility: "Başka Ajans", status_label: "Uygun değil", reason: "Başka bir ajansta", agencyStatus: "Uygun değil - Başka bir ajansta" },
  "pelinnncaannn": { eligibility: "Uygun Değil", status_label: "Uygun değil", reason: "Kriter dışı / İhlal", agencyStatus: "Uygun değil - Kriter dışı / İhlal" },
};

export function checkIsAdmin(user) {
  if (!user) return false;
  const role = String(user.role || "").toLowerCase().trim();
  const username = String(user.username || "").toLowerCase().trim();
  return (
    role === "admin" ||
    role === "superadmin" ||
    role === "system_admin" ||
    role === "manager" ||
    role === "employee" ||
    username === "admin" ||
    username === "superadmin"
  );
}

function safeUser() {
  if (typeof window === "undefined") return { username: "admin", role: "admin", fullName: "Admin Kullanıcı" };
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : { username: "admin", role: "admin", fullName: "Admin Kullanıcı" };
  } catch {
    return { username: "admin", role: "admin", fullName: "Admin Kullanıcı" };
  }
}

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

function normalizeUsername(value) {
  return String(value || "")
    .trim()
    .replace(/^@+/, "")
    .replace(/\s+/g, "")
    .toLocaleLowerCase("tr-TR");
}

function formatDateTr(dateStr) {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function calculateDaysSince(dateStr) {
  if (!dateStr) return 1;
  try {
    const d = new Date(String(dateStr).replace(" ", "T"));
    if (isNaN(d.getTime())) return 1;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - d.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays + 1);
  } catch {
    return 1;
  }
}

// 4 Genel Görüşme Aşaması (Tanışma, İlk Hafta Değerlendirmesi, Elmas Performans, Mezuniyet)
export const GENERAL_MEETING_STAGES = [
  {
    key: "stage_tanisma",
    legacyKeys: ["stage_1", "stage_tanisma"],
    label: "1. AŞAMA",
    title: "Tanışma Görüşmesi",
    icon: "🤝",
    desc: "İlk Tanışma & Kurulum",
    dayTarget: 1,
    badgeColor: "border-blue-500/30 bg-blue-500/10 text-blue-300",
  },
  {
    key: "stage_ilk_hafta",
    legacyKeys: ["stage_7", "stage_ilk_hafta", "stage_3"],
    label: "2. AŞAMA",
    title: "İlk Hafta Değerlendirmesi",
    icon: "📊",
    desc: "1. Hafta Yayın & Analiz",
    dayTarget: 7,
    badgeColor: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  },
  {
    key: "stage_elmas_performans",
    legacyKeys: ["stage_13", "stage_elmas_performans", "stage_10"],
    label: "3. AŞAMA",
    title: "Elmas Performans Görüşmesi",
    icon: "💎",
    desc: "Kademe, Hedef & Elmas",
    dayTarget: 13,
    badgeColor: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  },
  {
    key: "stage_mezuniyet",
    legacyKeys: ["stage_15", "stage_mezuniyet"],
    label: "4. AŞAMA",
    title: "Mezuniyet Görüşmesi",
    icon: "🎓",
    desc: "15. Gün Mezuniyet & Devir",
    dayTarget: 15,
    badgeColor: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  },
];

export const STAGES = GENERAL_MEETING_STAGES;

export function getStageData(followUpStages = {}, stageDef) {
  if (!followUpStages || typeof followUpStages !== "object") return {};
  if (followUpStages[stageDef.key]) return followUpStages[stageDef.key];
  if (stageDef.legacyKeys) {
    for (const lk of stageDef.legacyKeys) {
      if (followUpStages[lk]) return followUpStages[lk];
    }
  }
  return {};
}

function getInitialMockRows() {
  return [];
}

function mapBackendCandidateToRow(c) {
  const isOther = c.suitability_status === "baska_ajans" || c.status === "Başka Ajans" || c.suitability_reason === "Başka bir ajansta";
  const isRejected = c.suitability_status === "uygun_degil" || c.status === "Red" || c.status === "olumsuz";

  let eligibility = "Uygun";
  if (isOther) eligibility = "Başka Ajans";
  else if (isRejected) eligibility = "Uygun Değil";

  let status = c.status || "Keşif Kuyruğu";
  if (status === "yeni_aday") status = "Keşif Kuyruğu";
  if (status === "ajansa_katildi") status = "Kabul";

  let followUpStages = {};
  if (c.follow_up_stages) {
    if (typeof c.follow_up_stages === "string") {
      try {
        followUpStages = JSON.parse(c.follow_up_stages);
      } catch {}
    } else if (typeof c.follow_up_stages === "object") {
      followUpStages = c.follow_up_stages;
    }
  }

  return {
    id: Number(c.id),
    tiktok_username: String(c.tiktok_username || "").startsWith("@") ? c.tiktok_username : `@${c.tiktok_username || ""}`,
    full_name: c.display_name || c.full_name || "",
    query_date: c.query_date ? String(c.query_date).slice(0, 10) : (c.created_at ? String(c.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10)),
    created_by_username: c.created_by_username || c.owner_username || "admin",
    status: status,
    eligibility: eligibility,
    suitability_status: c.suitability_status || (eligibility === "Uygun" ? "uygun" : "uygun_degil"),
    suitability_reason: c.suitability_reason || (isOther ? "Başka bir ajansta" : isRejected ? "Kriter dışı / İhlal" : "Ajanssız (Davet Edilebilir)"),
    hidden_id: c.hidden_id || "",
    phone: c.phone || "",
    note: c.first_note || c.status_note || c.note || "",
    extra_info: c.extra_info || c.category || "",
    equipment_request: c.equipment_request || c.invitation_type || "Talep Yok",
    followers: Number(c.follower_count || c.followers || 0),
    avatar: c.profile_image_url || c.avatar || "",
    joined_at: c.joined_at ? String(c.joined_at).slice(0, 10) : "",
    manager_username: c.manager_username || c.assigned_manager || c.owner_username || "",
    intro_meeting_status: c.intro_meeting_status || c.agency_meeting || "beklemede",
    publisher_style: c.publisher_style || c.style || c.category || "Sohbet & Muhabbet",
    follow_up_stages: followUpStages
  };
}

function loadLocalScoutRows() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SCOUT);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalScoutRows(rows) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_SCOUT, JSON.stringify(rows));
  } catch (err) {
    console.error("Local scout verisi kaydedilemedi:", err);
  }
}

function loadAgencyCache() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AGENCY_CACHE);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAgencyCache(map) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_AGENCY_CACHE, JSON.stringify(map));
  } catch {}
}

/* ==========================================================================
   ANA BİLEŞEN: SCOUT TAKİP MERKEZİ (HAVUZ)
   ========================================================================== */
export default function ScoutCenterPage({
  embedded = false,
  section = "dashboard",
  currentAdmin = null,
  publisherRows = [],
  managerRows = [],
  onReload = null,
}) {
  const [currentUser, setCurrentUser] = useState(() => currentAdmin || safeUser());
  const [activeTab, setActiveTab] = useState(section === "scout_followup" ? "followup" : "pool");
  const [rows, setRows] = useState(loadLocalScoutRows);
  const [agencyCache, setAgencyCache] = useState(loadAgencyCache);
  const [selectedIds, setSelectedIds] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);

  // Sekme senkronizasyonu
  useEffect(() => {
    if (section === "scout_followup" || section === "followup") {
      setActiveTab("followup");
    } else if (section === "dashboard" || section === "scout_dashboard" || section === "pool") {
      setActiveTab("pool");
    }
  }, [section]);

  // Dynamic system managers list from API / props
  const [systemManagers, setSystemManagers] = useState(() => {
    if (Array.isArray(managerRows) && managerRows.length > 0) {
      const names = managerRows.map((m) => m.fullName || m.username).filter(Boolean);
      return [...new Set(names)];
    }
    return [];
  });

  // Mevcut takvim ayı
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(() => new Date().getMonth());

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [managerFilter, setManagerFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  // Pagination
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Expanded Hidden ID row ID
  const [expandedHiddenRowId, setExpandedHiddenRowId] = useState(null);
  const [hiddenIdInput, setHiddenIdInput] = useState("");
  const [hiddenIdSaving, setHiddenIdSaving] = useState(false);

  // Live Top Search State
  const [topSearchLoading, setTopSearchLoading] = useState(false);
  const [liveLookupResult, setLiveLookupResult] = useState(null);
  const [inlineLoadingId, setInlineLoadingId] = useState(null);

  // Modals
  const [candidateDetailModalRow, setCandidateDetailModalRow] = useState(null);
  const [followUpModalRow, setFollowUpModalRow] = useState(null);
  const [graduateModalRow, setGraduateModalRow] = useState(null);

  useEffect(() => {
    if (currentAdmin) {
      setCurrentUser(currentAdmin);
    } else {
      setCurrentUser(safeUser());
    }
  }, [currentAdmin]);

  const loadSystemManagers = useCallback(async () => {
    if (Array.isArray(managerRows) && managerRows.length > 0) {
      const names = managerRows.map((m) => m.fullName || m.username).filter(Boolean);
      setSystemManagers([...new Set(names)]);
      return;
    }
    try {
      const res = await fetch(API_USERS_LIST, { cache: "no-store" });
      const data = await res.json();
      if (data?.success && Array.isArray(data.users)) {
        const managers = data.users
          .filter((u) => {
            const r = String(u.role || "").toLowerCase();
            return r === "manager" || r === "admin" || r === "team_lead" || r === "scout_lead";
          })
          .map((u) => u.fullName || u.fullname || u.username)
          .filter(Boolean);

        const unique = [...new Set(managers)];
        if (unique.length > 0) {
          setSystemManagers(unique);
        }
      }
    } catch (err) {
      console.warn("Yöneticiler API'den alınamadı:", err);
    }
  }, [managerRows]);

  useEffect(() => {
    loadSystemManagers();
  }, [loadSystemManagers]);

  const loadCandidatesFromApi = useCallback(async () => {
    try {
      const res = await fetch(`${API_LIST}?tab=all`, { cache: "no-store" });
      const data = await res.json();
      if (data?.success && Array.isArray(data.candidates)) {
        const mapped = data.candidates.map(mapBackendCandidateToRow);
        setRows((prev) => {
          if (mapped.length === 0) return prev;
          const backendUsernames = new Set(mapped.map((m) => normalizeUsername(m.tiktok_username)));
          // Henüz API yanıtında görünmeyen yerel adayları koru
          const unpersisted = prev.filter((r) => !backendUsernames.has(normalizeUsername(r.tiktok_username)));
          const merged = [...mapped, ...unpersisted];
          saveLocalScoutRows(merged);
          return merged;
        });
      }
    } catch (err) {
      console.warn("Adaylar API'den yüklenemedi:", err);
    }
  }, []);

  useEffect(() => {
    loadCandidatesFromApi();
  }, [loadCandidatesFromApi]);

  const monthNames = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];

  const currentMonthLabel = `${monthNames[selectedMonthIndex]} ${selectedYear}`;

  const showToast = useCallback((text, type = "success") => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 4000);
  }, []);

  function handlePrevMonth() {
    setCurrentPage(1);
    if (selectedMonthIndex === 0) {
      setSelectedMonthIndex(11);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonthIndex((m) => m - 1);
    }
  }

  function handleNextMonth() {
    setCurrentPage(1);
    if (selectedMonthIndex === 11) {
      setSelectedMonthIndex(0);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonthIndex((m) => m + 1);
    }
  }

  const monthPrefix = `${selectedYear}-${String(selectedMonthIndex + 1).padStart(2, "0")}`;
  
  const monthRows = useMemo(() => {
    return rows.filter((r) => {
      const dateStr = r.query_date || r.joined_at || r.created_at || "";
      return dateStr.startsWith(monthPrefix);
    });
  }, [rows, monthPrefix]);

  const monthAcceptedRows = useMemo(() => {
    return rows.filter((r) => {
      const isAccepted = r.status === "Kabul" || r.status === "ajansa_katildi" || r.status === "mezun_edildi";
      if (!isAccepted) return false;
      const dateStr = r.joined_at || r.query_date || r.created_at || "";
      return dateStr.startsWith(monthPrefix);
    });
  }, [rows, monthPrefix]);

  const stats = useMemo(() => {
    const totalCandidates = monthRows.length;
    const totalAccepted = monthRows.filter((r) => r.status === "Kabul" || r.status === "ajansa_katildi" || r.status === "mezun_edildi").length;
    const totalRejected = monthRows.filter((r) => r.status === "Red" || r.eligibility === "Uygun Değil").length;
    const rate = totalCandidates > 0 ? Math.round((totalAccepted / totalCandidates) * 100) : 0;

    return {
      totalCandidates,
      totalAccepted,
      totalRejected,
      rate,
    };
  }, [monthRows]);

  const scoutManagers = useMemo(() => {
    const rowCreators = rows.map((r) => r.created_by_username).filter(Boolean);
    const combined = [...new Set([...systemManagers, ...rowCreators])];
    return combined.length ? combined : ["admin", "SenaBaran", "Zafer AKAN"];
  }, [rows, systemManagers]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const dateStr = row.query_date || row.joined_at || row.created_at || "";
      if (!dateStr.startsWith(monthPrefix)) {
        return false;
      }

      if (searchQuery.trim()) {
        const q = normalizeUsername(searchQuery);
        const searchTarget = normalizeUsername(`${row.tiktok_username} ${row.full_name} ${row.phone} ${row.note} ${row.extra_info} ${row.hidden_id}`);
        if (!searchTarget.includes(q)) return false;
      }

      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }

      if (managerFilter !== "all" && row.created_by_username !== managerFilter) {
        return false;
      }

      if (dateFilter && row.query_date !== dateFilter) {
        return false;
      }

      return true;
    });
  }, [rows, monthPrefix, searchQuery, statusFilter, managerFilter, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const isAllSelected = paginatedRows.length > 0 && paginatedRows.every((r) => selectedIds.includes(r.id));

  function handleToggleSelectAll() {
    if (isAllSelected) {
      setSelectedIds((prev) => prev.filter((id) => !paginatedRows.some((r) => r.id === id)));
    } else {
      const pageIds = paginatedRows.map((r) => r.id);
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  }

  function handleToggleRowSelect(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  const handleDeleteCandidate = useCallback(async (row) => {
    if (!row) return;
    const uname = row.tiktok_username || "Aday";
    if (!window.confirm(`${uname} kaydını kalıcı olarak silmek istediğinizden emin misiniz?`)) {
      return;
    }

    setRows((prev) => {
      const next = prev.filter((r) => r.id !== row.id && r.tiktok_username !== row.tiktok_username);
      saveLocalScoutRows(next);
      return next;
    });
    setSelectedIds((prev) => prev.filter((id) => id !== row.id));

    try {
      await fetch(API_DELETE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: row.id,
          tiktok_username: row.tiktok_username,
        }),
      });
      showToast(`${uname} başarıyla silindi.`, "info");
    } catch (err) {
      console.warn("Silme API hatası:", err);
      showToast(`${uname} yerel olarak silindi.`, "info");
    }
  }, [showToast]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedIds.length) return;
    const count = selectedIds.length;
    if (!window.confirm(`Seçilen ${count} adayı kalıcı olarak silmek istediğinizden emin misiniz?`)) {
      return;
    }

    const idsToDelete = [...selectedIds];
    setRows((prev) => {
      const next = prev.filter((r) => !idsToDelete.includes(r.id));
      saveLocalScoutRows(next);
      return next;
    });
    setSelectedIds([]);

    try {
      await fetch(API_DELETE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: idsToDelete }),
      });
      showToast(`${count} aday başarıyla silindi.`, "info");
    } catch (err) {
      console.warn("Toplu silme API hatası:", err);
      showToast(`${count} aday yerel olarak silindi.`, "info");
    }
  }, [selectedIds, showToast]);

  // Anlık satır içi düzenleme ve otomatik kaydetme
  const handleInlineUpdate = useCallback((id, updates) => {
    let targetRow = null;
    setRows((prev) => {
      const next = prev.map((r) => {
        if (r.id === id) {
          targetRow = { ...r, ...updates };
          return { ...r, ...updates, updated_at: new Date().toISOString() };
        }
        return r;
      });
      saveLocalScoutRows(next);
      return next;
    });

    try {
      const payload = {
        id,
        tiktok_username: targetRow?.tiktok_username || updates.tiktok_username,
        ...updates,
      };
      fetch(API_STATUS_UPDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.success && data.id && Number(data.id) !== Number(id)) {
            const realId = Number(data.id);
            setRows((prev) => {
              const next = prev.map((r) => (r.id === id ? { ...r, id: realId } : r));
              saveLocalScoutRows(next);
              return next;
            });
          }
        })
        .catch(() => {});
    } catch {}
  }, []);

  function handleStatusChange(row, newStatus) {
    if (newStatus === "Kabul") {
      setExpandedHiddenRowId(row.id);
      setHiddenIdInput(row.hidden_id || "");
      handleInlineUpdate(row.id, { status: "Kabul", eligibility: "Uygun", suitability_status: "uygun" });
    } else {
      if (expandedHiddenRowId === row.id) {
        setExpandedHiddenRowId(null);
      }
      const isOtherAgency = newStatus === "Başka Ajans";
      const isRejected = newStatus === "Red";
      const eligibility = isOtherAgency ? "Başka Ajans" : isRejected ? "Uygun Değil" : "Uygun";
      const reason = isOtherAgency ? "Başka bir ajansta" : isRejected ? "Kriter dışı / İhlal" : "Ajanssız (Davet Edilebilir)";

      handleInlineUpdate(row.id, {
        status: newStatus,
        eligibility,
        suitability_status: isOtherAgency ? "baska_ajans" : isRejected ? "uygun_degil" : "uygun",
        suitability_reason: reason,
      });

      // Persist to agency statuses API
      if (isOtherAgency || isRejected) {
        fetch(API_AGENCY_STATUS_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: row.tiktok_username,
            eligibility,
            status_label: "Uygun değil",
            reason,
            verified_by: currentUser?.username || "admin",
          }),
        }).catch(() => {});
      }

      showToast(`${row.tiktok_username} durumu '${newStatus}' olarak güncellendi.`);
    }
  }

  function handleQuickAccept(row) {
    setExpandedHiddenRowId(row.id);
    setHiddenIdInput(row.hidden_id || "");
    handleInlineUpdate(row.id, { status: "Kabul", eligibility: "Uygun", suitability_status: "uygun" });
    showToast(`${row.tiktok_username} için Hidden ID alanını doldurunuz.`, "info");
  }

  function handleQuickReject(row) {
    handleInlineUpdate(row.id, {
      status: "Red",
      eligibility: "Uygun Değil",
      suitability_status: "uygun_degil",
      suitability_reason: "Kriter dışı / İhlal",
    });
    fetch(API_AGENCY_STATUS_SAVE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: row.tiktok_username,
        eligibility: "Uygun Değil",
        status_label: "Uygun değil",
        reason: "Kriter dışı / İhlal",
        verified_by: currentUser?.username || "admin",
      }),
    }).catch(() => {});
    if (expandedHiddenRowId === row.id) setExpandedHiddenRowId(null);
    showToast(`${row.tiktok_username} reddedildi olarak işaretlendi.`, "error");
  }

  async function handleSaveHiddenId(row) {
    const trimmedHidden = hiddenIdInput.trim();
    if (!trimmedHidden) {
      showToast("Kabul edilen adaylar için Hidden ID zorunludur!", "error");
      return;
    }

    try {
      setHiddenIdSaving(true);
      const assignedManager = row.manager_username || row.created_by_username || currentUser?.fullName || currentUser?.username || "admin";
      const joinedDate = row.joined_at || new Date().toISOString().slice(0, 10);
      const initialStages = row.follow_up_stages && Object.keys(row.follow_up_stages).length > 0
        ? row.follow_up_stages
        : {
            stage_tanisma: {
              done: true,
              date: joinedDate,
              by: currentUser?.fullName || currentUser?.username || "admin",
              note: "Ajansa kabul edildi. 15 günlük oryantasyon ve tanışma süreci başlatıldı.",
            },
          };

      const updatedRow = {
        ...row,
        status: "Kabul",
        eligibility: "Uygun",
        suitability_status: "uygun",
        suitability_reason: "Ajanssız (Davet Edilebilir)",
        hidden_id: trimmedHidden,
        joined_at: joinedDate,
        manager_username: assignedManager,
        intro_meeting_status: row.intro_meeting_status || "beklemede",
        publisher_style: row.publisher_style || "Sohbet & Muhabbet",
        follow_up_stages: initialStages,
      };

      const nextRows = rows.map((r) => (r.id === row.id ? updatedRow : r));
      setRows(nextRows);
      saveLocalScoutRows(nextRows);
      setExpandedHiddenRowId(null);

      // Save permanently to scout_candidates MySQL database
      try {
        const updateRes = await fetch(API_STATUS_UPDATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: row.id,
            tiktok_username: row.tiktok_username,
            status: "Kabul",
            eligibility: "Uygun",
            suitability_status: "uygun",
            suitability_reason: "Ajanssız (Davet Edilebilir)",
            hidden_id: trimmedHidden,
            manager_username: assignedManager,
            joined_at: joinedDate,
            follow_up_stages: initialStages,
            intro_meeting_status: updatedRow.intro_meeting_status,
            publisher_style: updatedRow.publisher_style,
          }),
        });
        const updateData = await updateRes.json();
        if (updateData?.success && updateData.id && Number(updateData.id) !== Number(row.id)) {
          const realId = Number(updateData.id);
          setRows((prev) => {
            const next = prev.map((r) => (r.id === row.id ? { ...r, id: realId } : r));
            saveLocalScoutRows(next);
            return next;
          });
        }
      } catch (err) {
        console.warn("Aday durum güncelleme API hatası:", err);
      }

      try {
        const cleanUser = normalizeUsername(row.tiktok_username);
        const displayName = row.full_name?.trim() || cleanUser;
        const generatedPassword = `Wolf${Math.floor(1000 + Math.random() * 9000)}!`;

        await fetch(API_USER_ADD, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: cleanUser,
            fullName: displayName,
            password: generatedPassword,
            role: "publisher",
            phone: row.phone || "",
            hiddenId: trimmedHidden,
            representative: assignedManager,
            powerBIRole: "Aktif",
            status: "Aktif",
          }),
        });
      } catch (err) {
        console.warn("Otomatik kullanıcı kaydı:", err);
      }

      if (typeof onReload === "function") {
        try {
          onReload();
        } catch {}
      }

      showToast(
        `✓ ${row.tiktok_username} ajansa kabul edildi, 15 Günlük Takibe aktarıldı ve yayıncı olarak sisteme eklendi! (Yönetici: ${assignedManager})`,
        "success"
      );
    } catch (err) {
      showToast("Kayıt sırasında hata oluştu: " + err.message, "error");
    } finally {
      setHiddenIdSaving(false);
    }
  }

  const queryTikTokAndBackstage = useCallback(async (username) => {
    const clean = normalizeUsername(username);
    if (!clean) throw new Error("Geçerli bir TikTok kullanıcı adı giriniz.");

    const existingCandidate = rows.find((r) => normalizeUsername(r.tiktok_username) === clean);
    const cachedStatus = agencyCache[clean] || KNOWN_BACKSTAGE_AGENCY_MAP[clean];

    let initialEligibility = "Doğrulama Bekliyor";
    let initialStatusLabel = "Doğrulama Bekliyor";
    let initialReason = "Backstage'de Kontrol Edin";
    let initialAgencyStatus = "Doğrulama Bekliyor - Backstage Kontrolü Yapın";
    let initialIsVerified = false;

    if (cachedStatus) {
      initialEligibility = cachedStatus.eligibility;
      initialStatusLabel = cachedStatus.status_label || (cachedStatus.eligibility === "Uygun" ? "Uygun" : "Uygun değil");
      initialReason = cachedStatus.reason || (cachedStatus.eligibility === "Başka Ajans" ? "Başka bir ajansta" : (cachedStatus.eligibility === "Uygun" ? "Ajanssız (Davet Edilebilir)" : "Kriter dışı / İhlal"));
      initialAgencyStatus = `${initialStatusLabel} - ${initialReason}`;
      initialIsVerified = true;
    } else if (existingCandidate) {
      if (existingCandidate.eligibility === "Başka Ajans" || existingCandidate.status === "Başka Ajans") {
        initialEligibility = "Başka Ajans";
        initialStatusLabel = "Uygun değil";
        initialReason = existingCandidate.suitability_reason || "Başka bir ajansta";
        initialIsVerified = true;
      } else if (existingCandidate.eligibility === "Uygun Değil" || existingCandidate.status === "Red") {
        initialEligibility = "Uygun Değil";
        initialStatusLabel = "Uygun değil";
        initialReason = existingCandidate.suitability_reason || "Kriter dışı / İhlal";
        initialIsVerified = true;
      } else if (existingCandidate.eligibility === "Uygun" || existingCandidate.suitability_status === "uygun") {
        initialEligibility = "Uygun";
        initialStatusLabel = "Uygun";
        initialReason = "Ajanssız (Davet Edilebilir)";
        initialIsVerified = true;
      }
      initialAgencyStatus = `${initialStatusLabel} - ${initialReason}`;
    }

    let profile = {
      username: `@${clean}`,
      nickname: clean,
      avatar: "",
      followers: 0,
      likes: 0,
      bio: "",
      eligibility: initialEligibility,
      status_label: initialStatusLabel,
      reason: initialReason,
      agencyStatus: initialAgencyStatus,
      isVerified: initialIsVerified,
      isDuplicate: Boolean(existingCandidate),
      existingCandidate,
    };

    try {
      const res = await fetch(`${API_TIKTOK_PROFILE}?username=${encodeURIComponent(clean)}`, {
        cache: "no-store",
      });
      const data = await res.json();
      if (data?.success && data.data) {
        profile.avatar = data.data.avatar || "";
        profile.nickname = data.data.nickname || data.data.fullName || clean;
        profile.followers = safeNumber(data.data.followerCount || data.data.followers || 0);
        profile.likes = safeNumber(data.data.heartCount || data.data.likes || 0);
        profile.bio = data.data.bio || "";
        if (data.data.eligibility) {
          profile.eligibility = data.data.eligibility;
          profile.status_label = data.data.status_label || (data.data.eligibility === "Uygun" ? "Uygun" : "Uygun değil");
          profile.reason = data.data.reason || (data.data.eligibility === "Başka Ajans" ? "Başka bir ajansta" : (data.data.eligibility === "Uygun" ? "Ajanssız (Davet Edilebilir)" : "Kriter dışı / İhlal"));
          profile.agencyStatus = `${profile.status_label} - ${profile.reason}`;
          profile.isVerified = Boolean(data.data.isVerified);
        }
      }
    } catch (err) {
      console.warn("Profil fetch hatası:", err);
    }

    return profile;
  }, [rows, agencyCache]);

  async function handleTopSearchExecute(e) {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      showToast("Lütfen sorgulamak veya aramak için bir kullanıcı adı girin.", "info");
      return;
    }

    try {
      setTopSearchLoading(true);
      const result = await queryTikTokAndBackstage(query);
      setLiveLookupResult(result);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setTopSearchLoading(false);
    }
  }

  async function handleSetBackstageStatus(eligibilityType, customReason = "") {
    if (!liveLookupResult) return;
    const clean = normalizeUsername(liveLookupResult.username);
    const statusLabel = eligibilityType === "Uygun" ? "Uygun" : "Uygun değil";
    const reason = customReason || (
      eligibilityType === "Başka Ajans"
        ? "Başka bir ajansta"
        : eligibilityType === "Uygun Değil"
        ? "Kriter dışı / İhlal"
        : "Ajanssız (Davet Edilebilir)"
    );
    const agencyStatus = `${statusLabel} - ${reason}`;

    const updated = {
      ...liveLookupResult,
      eligibility: eligibilityType,
      status_label: statusLabel,
      reason: reason,
      agencyStatus: agencyStatus,
      isVerified: true,
    };
    setLiveLookupResult(updated);

    const newCache = {
      ...agencyCache,
      [clean]: { eligibility: eligibilityType, status_label: statusLabel, reason, agencyStatus },
    };
    setAgencyCache(newCache);
    saveAgencyCache(newCache);

    // Save permanently to MySQL server
    try {
      await fetch(API_AGENCY_STATUS_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: liveLookupResult.username,
          eligibility: eligibilityType,
          status_label: statusLabel,
          reason: reason,
          verified_by: currentUser?.username || "admin",
        }),
      });
    } catch (err) {
      console.warn("Ajans durumu sunucuya kaydedilemedi:", err);
    }

    // Update candidate row if present in table
    setRows((prev) =>
      prev.map((r) =>
        normalizeUsername(r.tiktok_username) === clean
          ? {
              ...r,
              eligibility: eligibilityType,
              suitability_status: eligibilityType === "Uygun" ? "uygun" : "uygun_degil",
              suitability_reason: reason,
              status: eligibilityType === "Başka Ajans" ? "Başka Ajans" : eligibilityType === "Uygun Değil" ? "Red" : r.status,
            }
          : r
      )
    );

    showToast(`✓ ${liveLookupResult.username} Backstage durumu '${statusLabel} (${reason})' olarak kaydedildi.`, eligibilityType === "Uygun" ? "success" : "info");
  }

  function handleOpenBackstage(username) {
    const clean = normalizeUsername(username);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(clean).catch(() => {});
    }
    window.open("https://live-backstage.tiktok.com/portal/anchor/relation", "_blank");
    showToast(`📋 '${clean}' panoya kopyalandı! Backstage sekmesinde aratarak sonucu doğrulayabilirsiniz.`, "info");
  }

  function handleAddCandidateFromLive(profileData) {
    if (profileData.eligibility === "Başka Ajans") {
      showToast("Bu yayıncı TikTok Backstage'de başka bir ajansa bağlı olduğu için aktif havuza eklenemez!", "error");
      return;
    }

    if (profileData.isDuplicate) {
      showToast(`Bu aday zaten havuzda kayıtlı! (Ekleyen: ${profileData.existingCandidate?.created_by_username || "admin"})`, "info");
      return;
    }

    const currentDayPad = String(new Date().getDate()).padStart(2, "0");
    const finalEligibility = (profileData.eligibility === "Doğrulama Bekliyor" || !profileData.eligibility) ? "Uygun" : profileData.eligibility;
    const addingUser = currentUser?.fullName || currentUser?.username || "admin";
    const newCandidate = {
      id: Date.now(),
      tiktok_username: profileData.username.startsWith("@") ? profileData.username : `@${profileData.username}`,
      full_name: profileData.nickname || "",
      query_date: `${monthPrefix}-${currentDayPad}`,
      created_by_username: addingUser,
      manager_username: addingUser, // Sisteme ekleyen yönetici direkt bu alana atanır
      status: finalEligibility === "Uygun Değil" ? "Red" : "Keşif Kuyruğu",
      eligibility: finalEligibility,
      suitability_status: finalEligibility === "Uygun" ? "uygun" : "uygun_degil",
      suitability_reason: finalEligibility === "Uygun" ? "Ajanssız (Davet Edilebilir)" : "Kriter dışı / İhlal",
      hidden_id: "",
      phone: "",
      note: profileData.bio ? `Biyografi: ${profileData.bio}` : "",
      extra_info: "",
      equipment_request: "Talep Yok",
      followers: profileData.followers || 0,
      avatar: profileData.avatar || "",
      intro_meeting_status: "beklemede",
      publisher_style: "Sohbet & Muhabbet",
    };

    const nextRows = [newCandidate, ...rows];
    setRows(nextRows);
    saveLocalScoutRows(nextRows);
    setLiveLookupResult(null);
    setSearchQuery("");
    showToast(`✓ ${newCandidate.tiktok_username} başarıyla ${currentMonthLabel} havuzuna eklendi!`, "success");

    try {
      fetch(API_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newCandidate),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.success && data.id) {
            const realId = Number(data.id);
            setRows((prev) => {
              const updated = prev.map((r) =>
                r.id === newCandidate.id || r.tiktok_username === newCandidate.tiktok_username
                  ? { ...r, id: realId }
                  : r
              );
              saveLocalScoutRows(updated);
              return updated;
            });
          }
        })
        .catch(() => {});
    } catch {}

    try {
      fetch(API_AGENCY_STATUS_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: newCandidate.tiktok_username,
          eligibility: finalEligibility,
          status_label: finalEligibility === "Uygun" ? "Uygun" : "Uygun değil",
          reason: finalEligibility === "Uygun" ? "Ajanssız (Davet Edilebilir)" : "Kriter dışı / İhlal",
          verified_by: currentUser?.username || "admin",
        }),
      }).catch(() => {});
    } catch {}
  }

  async function handleInlineQuery(row) {
    try {
      setInlineLoadingId(row.id);
      const profile = await queryTikTokAndBackstage(row.tiktok_username);

      const updates = {
        avatar: profile.avatar || row.avatar,
        full_name: row.full_name || profile.nickname,
        followers: profile.followers || row.followers,
        eligibility: profile.eligibility,
        note: row.note || profile.bio,
      };

      handleInlineUpdate(row.id, updates);
      showToast(
        `✓ ${row.tiktok_username} profili güncellendi: ${profile.agencyStatus || profile.eligibility} (${formatNumber(profile.followers)} Takipçi)`,
        profile.eligibility === "Uygun" ? "success" : "info"
      );
    } catch (err) {
      showToast(`${row.tiktok_username} sorgulanamadı: ${err.message}`, "error");
    } finally {
      setInlineLoadingId(null);
    }
  }

  function handleSaveStageNote(rowId, stageKey, stageData) {
    let targetCandidate = null;
    let nextStages = null;
    const updated = rows.map((r) => {
      if (r.id === rowId) {
        const stages = { ...(r.follow_up_stages || {}) };
        stages[stageKey] = {
          ...stageData,
          updatedAt: new Date().toISOString(),
        };
        targetCandidate = r;
        nextStages = stages;
        return { ...r, follow_up_stages: stages };
      }
      return r;
    });
    setRows(updated);
    saveLocalScoutRows(updated);
    setFollowUpModalRow(null);
    showToast("Görüşme kaydı başarıyla kaydedildi.", "success");

    if (targetCandidate && nextStages) {
      try {
        fetch(API_STATUS_UPDATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: rowId,
            tiktok_username: targetCandidate.tiktok_username,
            follow_up_stages: nextStages,
          }),
        }).catch(() => {});
      } catch {}
    }
  }

  async function handleGraduate(rowId, managerName) {
    const targetRow = rows.find((r) => r.id === rowId);
    const graduationNote = `${targetRow?.note || ""} [15. Gün Mezun Edildi -> Kalıcı Yönetici: ${managerName}]`;
    const updated = rows.map((r) => {
      if (r.id === rowId) {
        return {
          ...r,
          manager_username: managerName,
          status: "mezun_edildi",
          manager_assigned_at: new Date().toISOString(),
          note: graduationNote,
        };
      }
      return r;
    });
    setRows(updated);
    saveLocalScoutRows(updated);
    setGraduateModalRow(null);

    if (targetRow) {
      try {
        await fetch(API_STATUS_UPDATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: rowId,
            tiktok_username: targetRow.tiktok_username,
            status: "mezun_edildi",
            manager_username: managerName,
            note: graduationNote,
          }),
        });
      } catch {}

      try {
        const cleanUser = normalizeUsername(targetRow.tiktok_username);
        await fetch("/api/user-update.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: cleanUser,
            representative: managerName,
            status: "Aktif",
          }),
        });
      } catch (err) {
        console.warn("Yayıncı mezuniyet yönetici güncelleme:", err);
      }
    }

    if (typeof onReload === "function") {
      try {
        onReload();
      } catch {}
    }

    showToast(`✓ Yayıncı başarıyla mezun edildi ve ${managerName} yöneticisine devredildi.`, "success");
  }

  return (
    <div className="w-full space-y-6 pb-16 font-sans text-slate-100">
      {/* TOAST BİLDİRİMİ */}
      {toastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-2xl px-5 py-3.5 text-xs font-black shadow-2xl transition-all animate-bounce ${
            toastMessage.type === "error"
              ? "border border-red-500/40 bg-red-950/90 text-red-200"
              : toastMessage.type === "info"
              ? "border border-cyan-500/40 bg-cyan-950/90 text-cyan-200"
              : "border border-emerald-500/40 bg-emerald-950/90 text-emerald-200"
          }`}
        >
          <span>{toastMessage.type === "error" ? "❌" : toastMessage.type === "info" ? "ℹ️" : "✓"}</span>
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* ÜST BAŞLIK */}
      <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-white/[0.04] via-white/[0.02] to-transparent p-5 backdrop-blur-md">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-yellow-400">
            WOLF MEDIA YÖNETİM MERKEZİ
          </div>
          <h1 className="mt-1 flex items-center gap-3 text-2xl font-black tracking-tight text-white sm:text-3xl">
            <span>{activeTab === "pool" ? "Yayıncı Aday Takip Merkezi - Havuz" : "15 Günlük Yayıncı Takibi"}</span>
            <span className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-0.5 text-xs font-bold text-yellow-300">
              {activeTab === "pool" ? `${filteredRows.length} Aday (${currentMonthLabel})` : `${monthAcceptedRows.length} Takipteki Yayıncı (${currentMonthLabel})`}
            </span>
          </h1>
          <p className="mt-1 max-w-3xl text-xs text-white/50">
            {activeTab === "pool"
              ? "Yayıncı aday kayıtları bu sayfada listelenir. Adayların profil görselleri ve ajans uygunluğu sorgulanır, bilgiler düzenlenerek havuzda takip edilir."
              : "Ajansa kabul edilen yayıncıların 15 günlük oryantasyon ve 6 aşamalı görüşme takip süreci. 15. günü tamamlayan yayıncılar kalıcı yöneticilere atanarak mezun edilir."}
          </p>
        </div>
      </div>

      {/* SEKME GEÇİŞİ & AY GEZGİNİ */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex items-center gap-2 rounded-2xl border border-yellow-500/30 bg-black/50 p-1.5 shadow-lg">
          <button
            type="button"
            onClick={handlePrevMonth}
            title="Önceki Ay"
            className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/5 text-white/80 hover:bg-yellow-500 hover:text-black font-black text-sm transition"
          >
            ‹
          </button>
          <div className="flex items-center gap-2 px-3 text-xs font-black text-yellow-300">
            <span>📅</span>
            <span>{currentMonthLabel}</span>
          </div>
          <button
            type="button"
            onClick={handleNextMonth}
            title="Sonraki Ay"
            className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/5 text-white/80 hover:bg-yellow-500 hover:text-black font-black text-sm transition"
          >
            ›
          </button>
        </div>

        <div className="inline-flex rounded-2xl border border-white/10 bg-black/40 p-1.5 shadow-md">
          <button
            type="button"
            onClick={() => setActiveTab("pool")}
            className={`rounded-xl px-4 py-2 text-xs font-black transition ${
              activeTab === "pool"
                ? "bg-yellow-500 text-black shadow-lg"
                : "text-white/60 hover:text-white"
            }`}
          >
            📋 Yayıncı Aday Havuzu ({filteredRows.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("followup")}
            className={`rounded-xl px-4 py-2 text-xs font-black transition ${
              activeTab === "followup"
                ? "bg-yellow-500 text-black shadow-lg"
                : "text-white/60 hover:text-white"
            }`}
          >
            ⏱️ 15 Günlük Yayıncı Takibi ({monthAcceptedRows.length})
          </button>
        </div>
      </div>

      {activeTab === "pool" && (
        <>
          {/* 4'LÜ ÖZET KPI KARTLARI */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative overflow-hidden rounded-[1.8rem] border border-blue-500/20 bg-gradient-to-br from-blue-500/[0.08] via-black/40 to-black/30 p-5 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-blue-500/30 bg-blue-500/15 text-xl text-blue-400">
                  👥
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-blue-300">
                    {monthNames[selectedMonthIndex].toUpperCase()} AYI ADAYLARI
                  </div>
                  <div className="mt-0.5 text-2xl font-black text-white">{stats.totalCandidates}</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/50">{currentMonthLabel} döneminde eklenen adaylar</div>
            </div>

            <div className="relative overflow-hidden rounded-[1.8rem] border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.08] via-black/40 to-black/30 p-5 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/15 text-xl text-emerald-400">
                  ✓
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-emerald-300">
                    {monthNames[selectedMonthIndex].toUpperCase()} AYI KATILANLAR
                  </div>
                  <div className="mt-0.5 text-2xl font-black text-white">{stats.totalAccepted}</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/50">{currentMonthLabel} ajansa kabul edilenler</div>
            </div>

            <div className="relative overflow-hidden rounded-[1.8rem] border border-red-500/20 bg-gradient-to-br from-red-500/[0.08] via-black/40 to-black/30 p-5 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/15 text-xl text-red-400">
                  ✕
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-red-300">
                    REDDEDENLER
                  </div>
                  <div className="mt-0.5 text-2xl font-black text-white">{stats.totalRejected}</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/50">Adaylıktan vazgeçen veya uygun değil</div>
            </div>

            <div className="relative overflow-hidden rounded-[1.8rem] border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.08] via-black/40 to-black/30 p-5 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-500/30 bg-amber-500/15 text-xl text-amber-300 font-black">
                  %
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-amber-300">
                    KABUL ORANI
                  </div>
                  <div className="mt-0.5 text-2xl font-black text-white">%{stats.rate}</div>
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/50">
                Kabul eden / toplam keşif ({stats.totalAccepted} / {stats.totalCandidates})
              </div>
            </div>
          </div>

          {/* ARAMA & CANLI DOĞRULAMA */}
          <div className="space-y-3 rounded-3xl border border-white/10 bg-black/40 p-4 shadow-xl">
            <form onSubmit={handleTopSearchExecute} className="flex flex-col gap-3 xl:flex-row xl:items-center">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">🔍</span>
                <input
                  id="scout-top-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Kullanıcı adı, isim, telefon, not veya ek bilgi ara..."
                  className="h-12 w-full rounded-2xl border border-white/15 bg-black/60 pl-11 pr-24 text-xs text-white outline-none placeholder:text-white/30 focus:border-yellow-400 transition"
                />
                <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
                  <button
                    type="submit"
                    disabled={topSearchLoading || !searchQuery.trim()}
                    className="rounded-xl bg-yellow-500/20 border border-yellow-500/40 px-3 py-1.5 text-[11px] font-black text-yellow-300 hover:bg-yellow-500 hover:text-black transition disabled:opacity-30"
                  >
                    {topSearchLoading ? "Sorgulanıyor..." : "Sorgula"}
                  </button>
                  <span className="hidden sm:inline-block rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-mono text-white/40">
                    ⌘ K
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-12 rounded-2xl border border-white/15 bg-black/60 px-3.5 text-xs text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
                >
                  <option value="all">Tüm Durumlar</option>
                  <option value="Keşif Kuyruğu">Keşif Kuyruğu</option>
                  <option value="Düşünecek">Düşünecek</option>
                  <option value="Başka Ajans">Başka Ajans</option>
                  <option value="Kabul">Kabul</option>
                  <option value="Red">Red</option>
                </select>

                <select
                  value={managerFilter}
                  onChange={(e) => setManagerFilter(e.target.value)}
                  className="h-12 rounded-2xl border border-white/15 bg-black/60 px-3.5 text-xs text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
                >
                  <option value="all">Tüm Sorumlu Yöneticiler</option>
                  {scoutManagers.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>

                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="h-12 rounded-2xl border border-white/15 bg-black/60 px-3.5 text-xs text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
                />

                {(statusFilter !== "all" || managerFilter !== "all" || dateFilter || searchQuery) && (
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter("all");
                      setManagerFilter("all");
                      setDateFilter("");
                      setSearchQuery("");
                    }}
                    className="flex h-12 items-center gap-1 rounded-2xl border border-white/10 bg-white/5 px-3 text-xs font-bold text-white/60 hover:text-white transition"
                  >
                    <span>✕</span>
                    <span>Temizle</span>
                  </button>
                )}
              </div>
            </form>

            {liveLookupResult && (
              <div className="mt-3 overflow-hidden rounded-3xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/[0.08] via-black/90 to-yellow-500/[0.04] p-5 shadow-2xl backdrop-blur-md animate-fadeIn">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-yellow-500/40 bg-yellow-500/20 text-lg font-black text-yellow-300 overflow-hidden shadow-md">
                      {liveLookupResult.avatar ? (
                        <img
                          src={liveLookupResult.avatar}
                          alt={liveLookupResult.username}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        liveLookupResult.username.replace("@", "").slice(0, 2).toUpperCase()
                      )}
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-lg font-black text-white">{liveLookupResult.username}</span>

                        {/* TikTok Backstage Birebir Uyumlu Durum Kutusu */}
                        <div className="flex flex-col items-start">
                          {liveLookupResult.eligibility === "Başka Ajans" || liveLookupResult.reason === "Başka bir ajansta" ? (
                            <div>
                              <span className="inline-block rounded-md border border-[#ffcfc7] bg-[#ffeae6] px-2.5 py-0.5 text-xs font-black text-[#d93025] shadow-sm">
                                Uygun değil
                              </span>
                              <div className="mt-0.5 text-xs font-black text-[#ff6b6b]">
                                {liveLookupResult.reason || "Başka bir ajansta"}
                              </div>
                            </div>
                          ) : liveLookupResult.eligibility === "Uygun Değil" ? (
                            <div>
                              <span className="inline-block rounded-md border border-amber-500/40 bg-amber-500/20 px-2.5 py-0.5 text-xs font-black text-amber-300 shadow-sm">
                                Uygun değil
                              </span>
                              <div className="mt-0.5 text-xs font-black text-amber-300">
                                {liveLookupResult.reason || "Kriter dışı / İhlal"}
                              </div>
                            </div>
                          ) : liveLookupResult.eligibility === "Uygun" ? (
                            <div>
                              <span className="inline-block rounded-md border border-emerald-500/40 bg-emerald-500/20 px-2.5 py-0.5 text-xs font-black text-emerald-300 shadow-sm">
                                Uygun
                              </span>
                              <div className="mt-0.5 text-xs font-black text-emerald-400">
                                {liveLookupResult.reason || "Ajanssız (Davet Edilebilir)"}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span className="inline-block rounded-md border border-yellow-500/40 bg-yellow-500/20 px-2.5 py-0.5 text-xs font-black text-yellow-300 shadow-sm">
                                Doğrulama Bekliyor
                              </span>
                              <div className="mt-0.5 text-xs font-bold text-yellow-400/90">
                                Backstage'de Kontrol Edin
                              </div>
                            </div>
                          )}
                        </div>

                        {liveLookupResult.isDuplicate && (
                          <span className="rounded-full border border-orange-500/40 bg-orange-500/20 px-2.5 py-0.5 text-[10px] font-bold text-orange-300">
                            Havuzda Kayıtlı
                          </span>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-white/80">
                        <span>👤 İsim: <b className="text-white">{liveLookupResult.nickname || liveLookupResult.username}</b></span>
                        <span>👥 Takipçi: <b className="text-yellow-300">{formatNumber(liveLookupResult.followers)}</b></span>
                        {liveLookupResult.likes > 0 && (
                          <span>❤️ Beğeni: <b className="text-white/80">{formatNumber(liveLookupResult.likes)}</b></span>
                        )}
                        {liveLookupResult.bio && (
                          <span className="truncate max-w-xs text-white/50 italic">"{liveLookupResult.bio}"</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5 rounded-2xl border border-white/10 bg-black/60 p-1.5">
                      <button
                        type="button"
                        onClick={() => handleSetBackstageStatus("Başka Ajans", "Başka bir ajansta")}
                        title="TikTok Backstage: Uygun değil - Başka bir ajansta"
                        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black transition ${
                          liveLookupResult.eligibility === "Başka Ajans"
                            ? "bg-red-500 text-white shadow-lg scale-105"
                            : "text-red-400/80 hover:bg-red-500/20 hover:text-red-200"
                        }`}
                      >
                        <span>🔴</span>
                        <span>Başka Ajans</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSetBackstageStatus("Uygun", "Ajanssız (Davet Edilebilir)")}
                        title="TikTok Backstage: Uygun - Ajanssız"
                        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black transition ${
                          liveLookupResult.eligibility === "Uygun"
                            ? "bg-emerald-500 text-black shadow-lg scale-105"
                            : "text-emerald-400/80 hover:bg-emerald-500/20 hover:text-emerald-200"
                        }`}
                      >
                        <span>🟢</span>
                        <span>Ajanssız</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSetBackstageStatus("Uygun Değil", "Kriter dışı / İhlal")}
                        title="TikTok Backstage: Uygun değil - Kriter Dışı"
                        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black transition ${
                          liveLookupResult.eligibility === "Uygun Değil"
                            ? "bg-amber-500 text-black shadow-lg scale-105"
                            : "text-amber-400/80 hover:bg-amber-500/20 hover:text-amber-200"
                        }`}
                      >
                        <span>🟡</span>
                        <span>Kriter Dışı</span>
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleOpenBackstage(liveLookupResult.username)}
                      title="Kullanıcı adını panoya kopyalar ve TikTok LIVE Backstage portalını açar"
                      className="flex items-center gap-1.5 rounded-2xl border border-yellow-500/40 bg-yellow-500/10 px-3.5 py-2 text-xs font-bold text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
                    >
                      <span>🔗</span>
                      <span>Backstage'de Aç</span>
                    </button>

                    <button
                      type="button"
                      disabled={liveLookupResult.eligibility === "Başka Ajans"}
                      onClick={() => handleAddCandidateFromLive(liveLookupResult)}
                      className={`flex items-center gap-1.5 rounded-2xl px-4 py-2.5 text-xs font-black transition ${
                        liveLookupResult.eligibility === "Başka Ajans"
                          ? "cursor-not-allowed border border-red-500/30 bg-red-950/40 text-red-300/60"
                          : "bg-emerald-500 text-black shadow-lg hover:bg-emerald-400 active:scale-95"
                      }`}
                    >
                      <span>{liveLookupResult.eligibility === "Başka Ajans" ? "🚫" : "➕"}</span>
                      <span>
                        {liveLookupResult.eligibility === "Başka Ajans"
                          ? "Eklenemez (Başka Ajans)"
                          : "Havuza Aday Olarak Ekle"}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setLiveLookupResult(null)}
                      className="rounded-2xl border border-white/15 bg-white/5 p-2.5 text-xs text-white/60 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {liveLookupResult.eligibility === "Başka Ajans" && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-950/30 px-3.5 py-2 text-xs text-red-300">
                    <span>⚠️</span>
                    <span>Bu yayıncı TikTok LIVE Backstage'de başka bir ajansa bağlıdır. Mükerrer/geçersiz işlem olmaması için sisteme aday olarak eklenmesi engellenmiştir.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* TOPLU SEÇİM VE SİLME ÇUBUĞU */}
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/30 bg-red-950/40 p-3.5 text-xs text-red-200 backdrop-blur-md animate-fadeIn">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-red-500/20 text-xs font-black text-red-300">
                  {selectedIds.length}
                </span>
                <span className="font-bold">aday seçildi</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white transition"
                >
                  Seçimi Temizle
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-1.5 text-xs font-black text-white hover:bg-red-500 shadow-md active:scale-95 transition"
                >
                  <span>🗑️</span>
                  <span>Seçilenleri Sil ({selectedIds.length})</span>
                </button>
              </div>
            </div>
          )}

          {/* ADAY HAVUZU TABLOSU - PROFESYONEL VE YAZILABİLİR ALANLAR (ZOOM KORUMALI) */}
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/40 shadow-2xl backdrop-blur-md">
            <div className="w-full overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
              <table className="w-full min-w-[1550px] text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03] text-[11px] font-black uppercase tracking-wider text-white/50 select-none">
                    <th className="w-12 px-3 py-3.5 text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleToggleSelectAll}
                        className="h-4 w-4 rounded border-white/20 bg-black/60 accent-yellow-400 cursor-pointer"
                      />
                    </th>
                    <th className="w-12 px-2 py-3.5 text-center">#</th>
                    <th className="w-64 px-3 py-3.5 whitespace-nowrap">KULLANICI ADI ⇅</th>
                    <th className="w-48 px-3 py-3.5 whitespace-nowrap">İSİM ⇅</th>
                    <th className="w-36 px-3 py-3.5 whitespace-nowrap">SORGULANAN TARİH ⇅</th>
                    <th className="w-40 px-3 py-3.5 whitespace-nowrap">SORUMLU YÖNETİCİ ⇅</th>
                    <th className="w-44 px-3 py-3.5 whitespace-nowrap">TELEFON ⇅</th>
                    <th className="w-44 px-3 py-3.5 whitespace-nowrap">NOT ⇅</th>
                    <th className="w-44 px-3 py-3.5 whitespace-nowrap">EK BİLGİ ⇅</th>
                    <th className="w-36 px-3 py-3.5 whitespace-nowrap">EKİPMAN TALEP ⇅</th>
                    <th className="w-40 px-3 py-3.5 whitespace-nowrap">DURUM ⇅</th>
                    <th className="w-48 px-3 py-3.5 text-center whitespace-nowrap">İŞLEMLER</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-white/5">
                  {paginatedRows.map((row, idx) => {
                    const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                    const isSelected = selectedIds.includes(row.id);
                    const isExpanded = expandedHiddenRowId === row.id;

                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          className={`transition ${
                            isExpanded
                              ? "bg-emerald-500/[0.08]"
                              : isSelected
                              ? "bg-yellow-500/[0.06]"
                              : "hover:bg-white/[0.02]"
                          }`}
                        >
                          {/* 1. Checkbox */}
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleRowSelect(row.id)}
                              className="h-4 w-4 rounded border-white/20 bg-black/60 accent-yellow-400 cursor-pointer"
                            />
                          </td>

                          {/* 2. Row Number */}
                          <td className="px-2 py-3 text-center font-mono font-bold text-white/40">
                            {rowNumber}
                          </td>

                          {/* 3. Kullanıcı Adı + Avatar + Durum Rozeti + Sorgula Butonu */}
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2.5">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-yellow-500/30 bg-gradient-to-br from-amber-500/20 to-yellow-500/10 text-xs font-black text-yellow-300 shadow-sm overflow-hidden">
                                {row.avatar ? (
                                  <img
                                    src={row.avatar}
                                    alt={row.tiktok_username}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  row.tiktok_username.replace("@", "").slice(0, 2).toUpperCase()
                                )}
                              </div>

                              <div className="min-w-0 max-w-[140px]">
                                <a
                                  href={`https://www.tiktok.com/${row.tiktok_username}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="truncate block font-black text-white hover:text-yellow-300 transition text-xs"
                                >
                                  {row.tiktok_username}
                                </a>

                                <div className="mt-0.5 flex items-center gap-1">
                                  <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-bold ${
                                      row.eligibility === "Uygun"
                                        ? "text-emerald-400"
                                        : row.eligibility === "Başka Ajans"
                                        ? "text-red-400"
                                        : "text-amber-400"
                                    }`}
                                  >
                                    <span
                                      className={`h-1.5 w-1.5 rounded-full ${
                                        row.eligibility === "Uygun"
                                          ? "bg-emerald-400"
                                          : row.eligibility === "Başka Ajans"
                                          ? "bg-red-400"
                                          : "bg-amber-400"
                                      }`}
                                    />
                                    <span>{row.eligibility || "Uygun"}</span>
                                  </span>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => handleInlineQuery(row)}
                                disabled={inlineLoadingId === row.id}
                                title="TikTok profili ve durumunu anlık sorgula"
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xs font-bold text-white/80 hover:border-yellow-400 hover:text-white transition disabled:opacity-40"
                              >
                                {inlineLoadingId === row.id ? "..." : "🔍"}
                              </button>
                            </div>
                          </td>

                          {/* 4. İSİM (Yazılabilir Alan) */}
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={row.full_name || ""}
                              onChange={(e) => handleInlineUpdate(row.id, { full_name: e.target.value })}
                              placeholder="İsim girin..."
                              className="h-9 w-full min-w-[150px] rounded-xl border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white outline-none focus:border-yellow-400 focus:bg-black/80 transition placeholder:text-white/20 shadow-inner"
                            />
                          </td>

                          {/* 5. SORGULANAN TARİH */}
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex h-9 items-center justify-between gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 text-xs text-white/80 font-mono">
                              <span>{formatDateTr(row.query_date)}</span>
                              <span className="text-white/40">🗓️</span>
                            </div>
                          </td>

                          {/* 6. SCOUT YÖNETİCİ */}
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="flex h-9 items-center justify-between gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 text-xs font-bold text-white/80">
                              <span className="truncate max-w-[110px]">{row.created_by_username || "admin"}</span>
                              <span className="text-[11px] text-yellow-400/70">🔒</span>
                            </div>
                          </td>

                          {/* 7. TELEFON (Yazılabilir Alan) */}
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={row.phone || ""}
                              onChange={(e) => handleInlineUpdate(row.id, { phone: e.target.value })}
                              placeholder="+90 5XX XXX XX XX"
                              className="h-9 w-full min-w-[140px] rounded-xl border border-white/10 bg-black/40 px-3 text-xs font-mono font-semibold text-white outline-none focus:border-yellow-400 focus:bg-black/80 transition placeholder:text-white/20 shadow-inner"
                            />
                          </td>

                          {/* 8. NOT (Yazılabilir Alan) */}
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={row.note || ""}
                              onChange={(e) => handleInlineUpdate(row.id, { note: e.target.value })}
                              placeholder="Not ekle..."
                              className="h-9 w-full min-w-[140px] rounded-xl border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white outline-none focus:border-yellow-400 focus:bg-black/80 transition placeholder:text-white/20 shadow-inner"
                            />
                          </td>

                          {/* 9. EK BİLGİ (Yazılabilir Alan) */}
                          <td className="px-3 py-3">
                            <input
                              type="text"
                              value={row.extra_info || ""}
                              onChange={(e) => handleInlineUpdate(row.id, { extra_info: e.target.value })}
                              placeholder="Örn. şehir, yaş..."
                              className="h-9 w-full min-w-[140px] rounded-xl border border-white/10 bg-black/40 px-3 text-xs font-semibold text-white outline-none focus:border-yellow-400 focus:bg-black/80 transition placeholder:text-white/20 shadow-inner"
                            />
                          </td>

                          {/* 10. EKİPMAN TALEP (Seçilebilir Dropdown) */}
                          <td className="px-3 py-3">
                            <select
                              value={row.equipment_request || "Talep Yok"}
                              onChange={(e) => handleInlineUpdate(row.id, { equipment_request: e.target.value })}
                              className={`h-9 w-full min-w-[120px] rounded-xl border px-2.5 text-xs font-bold outline-none transition [color-scheme:dark] ${
                                row.equipment_request === "Talep Var"
                                  ? "border-amber-500/40 bg-amber-500/20 text-amber-300"
                                  : row.equipment_request === "Gönderildi"
                                  ? "border-emerald-500/40 bg-emerald-500/20 text-emerald-300"
                                  : row.equipment_request === "İnceleniyor"
                                  ? "border-cyan-500/40 bg-cyan-500/20 text-cyan-300"
                                  : "border-white/10 bg-black/60 text-white/70"
                              }`}
                            >
                              <option value="Talep Yok">Talep Yok</option>
                              <option value="Talep Var">Talep Var</option>
                              <option value="İnceleniyor">İnceleniyor</option>
                              <option value="Gönderildi">Gönderildi</option>
                            </select>
                          </td>

                          {/* 11. DURUM */}
                          <td className="px-3 py-3">
                            <select
                              value={row.status || "Keşif Kuyruğu"}
                              onChange={(e) => handleStatusChange(row, e.target.value)}
                              className={`h-9 w-full min-w-[130px] rounded-xl border px-3 text-xs font-black outline-none transition [color-scheme:dark] ${
                                row.status === "Kabul"
                                  ? "border-emerald-500/40 bg-emerald-500/20 text-emerald-300"
                                  : row.status === "Red"
                                  ? "border-red-500/40 bg-red-500/20 text-red-300"
                                  : row.status === "Başka Ajans"
                                  ? "border-red-500/40 bg-red-500/20 text-red-300"
                                  : row.status === "Düşünecek"
                                  ? "border-cyan-500/40 bg-cyan-500/20 text-cyan-300"
                                  : "border-white/15 bg-black/60 text-white"
                              }`}
                            >
                              <option value="Keşif Kuyruğu">Keşif Kuyruğu</option>
                              <option value="Düşünecek">Düşünecek</option>
                              <option value="Başka Ajans">Başka Ajans</option>
                              <option value="Kabul">Kabul</option>
                              <option value="Red">Red</option>
                            </select>
                          </td>

                          {/* 12. İŞLEMLER */}
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleQuickAccept(row)}
                                className={`flex h-8 items-center gap-1 rounded-xl border px-3 text-xs font-black transition ${
                                  row.status === "Kabul"
                                    ? "border-emerald-500 bg-emerald-500 text-black shadow-md"
                                    : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black"
                                }`}
                              >
                                <span>✓</span>
                                <span>Kabul</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleQuickReject(row)}
                                className="flex h-8 items-center gap-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 text-xs font-black text-red-300 hover:bg-red-500 hover:text-black transition"
                              >
                                <span>✕</span>
                                <span>Red</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => setCandidateDetailModalRow(row)}
                                title="Detayları Düzenle / İncele"
                                className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xs font-black text-white/60 hover:text-white transition"
                              >
                                ⋮
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteCandidate(row)}
                                title="Adayı Kalıcı Olarak Sil"
                                className="flex h-8 w-8 items-center justify-center rounded-xl border border-red-500/30 bg-red-500/10 text-xs text-red-400 hover:bg-red-500 hover:text-white transition"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* GENİŞLEYEN HIDDEN ID EKLEME ALANI */}
                        {isExpanded && (
                          <tr className="border-y border-emerald-500/30 bg-emerald-500/[0.07]">
                            <td colSpan={12} className="px-6 py-4">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-xs font-black text-emerald-300">
                                    <span>🔑</span>
                                    <span>Hidden ID Ekleme Alanı</span>
                                  </div>
                                  <div className="text-[11px] text-white/60">
                                    Kabul edilen adaylar için Hidden ID zorunludur. Kaydedildiğinde aday yayıncılar listesine eklenir ve 15 günlük oryantasyona başlar.
                                  </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="relative min-w-[260px]">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">🔍</span>
                                    <input
                                      type="text"
                                      autoFocus
                                      value={hiddenIdInput}
                                      onChange={(e) => setHiddenIdInput(e.target.value)}
                                      placeholder="Hidden ID girin (Örn: 7479023620...)"
                                      className="h-10 w-full rounded-xl border border-emerald-500/50 bg-black/80 pl-9 pr-3 text-xs font-mono font-bold text-white outline-none focus:border-emerald-400"
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    disabled={hiddenIdSaving}
                                    onClick={() => handleSaveHiddenId(row)}
                                    className="flex h-10 items-center gap-1.5 rounded-xl bg-emerald-500 px-5 text-xs font-black text-black shadow-lg hover:bg-emerald-400 active:scale-95 transition disabled:opacity-40"
                                  >
                                    <span>💾</span>
                                    <span>{hiddenIdSaving ? "Kaydediliyor..." : "Kaydet"}</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => setExpandedHiddenRowId(null)}
                                    className="flex h-10 items-center rounded-xl border border-white/15 bg-white/5 px-4 text-xs font-bold text-white/60 hover:text-white transition"
                                  >
                                    İptal
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {!paginatedRows.length && (
                    <tr>
                      <td colSpan={12} className="px-6 py-16 text-center text-sm text-white/40">
                        Seçili ayda (<b className="text-yellow-300">{currentMonthLabel}</b>) arama ve filtre kriterlerine uygun aday kaydı bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ALT SAYFALAMA */}
            <div className="flex flex-col gap-4 border-t border-white/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between text-xs text-white/60">
              <div>
                Toplam <b className="text-white">{filteredRows.length}</b> kayıt listeleniyor ({currentMonthLabel})
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span>Sayfa Başı:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="rounded-xl border border-white/15 bg-black/60 px-2.5 py-1.5 text-xs text-white outline-none [color-scheme:dark]"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:bg-white/15 hover:text-white transition disabled:opacity-30"
                  >
                    ‹
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(Math.max(0, currentPage - 3), currentPage + 2)
                    .map((pageNum) => (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`flex h-8 w-8 items-center justify-center rounded-xl text-xs font-black transition ${
                          currentPage === pageNum
                            ? "bg-amber-500 text-black shadow-md"
                            : "border border-white/10 bg-white/5 text-white/70 hover:bg-white/15 hover:text-white"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 hover:bg-white/15 hover:text-white transition disabled:opacity-30"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* SEKME 2: 15 GÜNLÜK YAYINCI TAKİBİ */}
      {activeTab === "followup" && (
        <Scout15DayFollowUpView
          rows={monthAcceptedRows}
          currentUser={currentUser}
          currentMonthLabel={currentMonthLabel}
          monthPrefix={monthPrefix}
          systemManagers={systemManagers}
          onOpenStageModal={(row, stageKey) => setFollowUpModalRow({ row, stageKey })}
          onGraduate={(row) => setGraduateModalRow(row)}
          onInlineUpdate={handleInlineUpdate}
          showToast={showToast}
        />
      )}

      {/* DETAY MODALI */}
      {candidateDetailModalRow && (
        <CandidateDetailModal
          row={candidateDetailModalRow}
          currentUser={currentUser}
          systemManagers={systemManagers}
          onClose={() => setCandidateDetailModalRow(null)}
          onSave={(updated) => {
            handleInlineUpdate(updated.id, updated);
            setCandidateDetailModalRow(null);
            showToast("Aday detayları güncellendi.", "success");
          }}
          onDelete={(id) => {
            const rowToDelete = rows.find((r) => r.id === id) || candidateDetailModalRow;
            setCandidateDetailModalRow(null);
            handleDeleteCandidate(rowToDelete);
          }}
        />
      )}

      {followUpModalRow && (
        <StageFollowUpModal
          row={followUpModalRow.row}
          stageKey={followUpModalRow.stageKey}
          currentUser={currentUser}
          onClose={() => setFollowUpModalRow(null)}
          onSave={(stageKey, stageData) => handleSaveStageNote(followUpModalRow.row.id, stageKey, stageData)}
        />
      )}

      {graduateModalRow && (
        <GraduateManagerModal
          row={graduateModalRow}
          currentUser={currentUser}
          systemManagers={systemManagers}
          onClose={() => setGraduateModalRow(null)}
          onSave={(managerName) => handleGraduate(graduateModalRow.id, managerName)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   GÖRÜNÜM 2: 15 GÜNLÜK YAYINCI TAKİBİ
   ========================================================================== */
function Scout15DayFollowUpView({
  rows = [],
  currentUser,
  currentMonthLabel,
  monthPrefix,
  systemManagers = [],
  onOpenStageModal,
  onGraduate,
  onInlineUpdate,
  showToast,
}) {
  const [filter, setFilter] = useState("all");
  const [selectedSlideOverPublisher, setSelectedSlideOverPublisher] = useState(null);
  const [allComms, setAllComms] = useState(loadAllCommunications);

  const isAdmin = checkIsAdmin(currentUser);

  useEffect(() => {
    function handleUpdate() {
      setAllComms(loadAllCommunications());
    }
    window.addEventListener("wolf_crm_updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("wolf_crm_updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  const enrichedRows = useMemo(() => {
    return rows.map((r) => {
      const daysCount = calculateDaysSince(r.joined_at || r.query_date);
      const isGraduated = r.status === "mezun_edildi";
      const stages = r.follow_up_stages || {};
      const completedCount = GENERAL_MEETING_STAGES.filter((s) => {
        const sData = getStageData(stages, s);
        return Boolean(sData.done);
      }).length;
      const progressPercent = Math.min(100, Math.round((completedCount / GENERAL_MEETING_STAGES.length) * 100));

      return {
        ...r,
        daysCount,
        isGraduated,
        completedCount,
        progressPercent,
      };
    }).sort((a, b) => b.daysCount - a.daysCount);
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === "ready_to_graduate") return enrichedRows.filter((r) => r.daysCount >= 15 && !r.isGraduated);
    if (filter === "graduated") return enrichedRows.filter((r) => r.isGraduated);
    if (filter === "in_progress") return enrichedRows.filter((r) => !r.isGraduated);
    return enrichedRows;
  }, [enrichedRows, filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/40 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-xs font-black text-yellow-300">
            <span>📅</span>
            <span>{currentMonthLabel}</span>
          </div>

          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
              filter === "all" ? "bg-white/20 text-white font-black" : "bg-white/5 text-white/70 hover:text-white"
            }`}
          >
            Tümü ({enrichedRows.length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("in_progress")}
            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
              filter === "in_progress" ? "bg-cyan-500 text-black font-black" : "bg-white/5 text-white/70 hover:text-white"
            }`}
          >
            Devam Edenler ({enrichedRows.filter((r) => !r.isGraduated).length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("ready_to_graduate")}
            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
              filter === "ready_to_graduate" ? "bg-amber-500 text-black font-black" : "bg-white/5 text-white/70 hover:text-white"
            }`}
          >
            Mezuniyete Hazır ({enrichedRows.filter((r) => r.daysCount >= 15 && !r.isGraduated).length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("graduated")}
            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
              filter === "graduated" ? "bg-emerald-500 text-black font-black" : "bg-white/5 text-white/70 hover:text-white"
            }`}
          >
            Mezun Edilenler ({enrichedRows.filter((r) => r.isGraduated).length})
          </button>
        </div>

        <div className="text-xs text-white/50">
          4 genel görüşme aşaması, ek görüşme kayıtları ve yönetici takibi bu ekrandan yürütülür.
        </div>
      </div>

      <div className="grid gap-5">
        {filtered.map((row) => {
          const pubComms = getCommunicationsForUser(allComms, row.tiktok_username, row.hidden_id);

          return (
            <div
              key={row.id}
              className="overflow-hidden rounded-3xl border border-white/10 bg-black/40 p-5 shadow-xl hover:border-yellow-500/30 transition space-y-4"
            >
              {/* ÜST BİLGİ & MEZUNİYET DURUMU */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-yellow-500/30 bg-yellow-500/10 text-base font-black text-yellow-300">
                    {row.avatar ? (
                      <img src={row.avatar} alt={row.tiktok_username} className="h-full w-full rounded-2xl object-cover" />
                    ) : (
                      row.tiktok_username.replace("@", "").slice(0, 2).toUpperCase()
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-black text-white">{row.tiktok_username}</span>
                      <span className="rounded-full border border-cyan-500/40 bg-cyan-500/20 px-2.5 py-0.5 text-[10px] font-black text-cyan-200">
                        {row.daysCount}. Gün
                      </span>
                      {row.isGraduated ? (
                        <span className="rounded-full border border-emerald-500/40 bg-emerald-500/20 px-2.5 py-0.5 text-[10px] font-black text-emerald-300">
                          🎓 Mezun (Yönetici: {row.manager_username})
                        </span>
                      ) : (
                        <span className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-0.5 text-[10px] font-black text-yellow-300">
                          🟡 15 Günlük Oryantasyon
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-white/60">
                      <span>👤 {row.full_name || "İsimsiz"}</span>
                      <span>📞 {row.phone || "-"}</span>
                      <span>🔑 Hidden ID: <b className="font-mono text-white">{row.hidden_id || "-"}</b></span>
                      <span>Ekleyen / Sorumlu: <b className="text-yellow-300">{row.created_by_username || "admin"}</b></span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs font-black text-white">Genel Süreç: %{row.progressPercent}</div>
                    <div className="text-[10px] text-white/40">{row.completedCount} / 4 Görüşme</div>
                  </div>

                  {!row.isGraduated && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isAdmin) {
                          onGraduate(row);
                        } else {
                          const mgr = row.manager_username || currentUser?.fullName || currentUser?.username || "admin";
                          if (window.confirm(`${row.tiktok_username} yayıncısının 15 günlük oryantasyonunu tamamlayıp kalıcı yöneticiniz (${mgr}) olarak mezun etmek istiyor musunuz?`)) {
                            onGraduate(row);
                          }
                        }
                      }}
                      className="flex items-center gap-1.5 rounded-2xl bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-2.5 text-xs font-black text-black shadow-lg hover:brightness-110 active:scale-95 transition"
                    >
                      <span>🎓</span>
                      <span>{isAdmin ? "Yönetici Ata & Mezun Et" : "Oryantasyonu Tamamla & Mezun Et"}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* YÖNETİCİ, TANITIM GÖRÜŞMESİ, TARZ VE İLETİŞİM CRM KONTROL BARI */}
              <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* 1. BAĞLI YÖNETİCİ (YETKİ SADECE ADMİNDE) */}
                  <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-black/40 px-3 py-1.5">
                    <span className="text-[11px] font-bold text-yellow-400">👑 Bağlı Yönetici:</span>
                    {isAdmin ? (
                      <select
                        value={row.manager_username || ""}
                        onChange={(e) => {
                          const nextManager = e.target.value;
                          onInlineUpdate?.(row.id, { manager_username: nextManager });
                          showToast?.(`${row.tiktok_username} için sorumlu yönetici '${nextManager}' olarak güncellendi.`);
                        }}
                        className="rounded-lg bg-black/80 border border-white/15 px-2 py-0.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
                      >
                        <option value="">Yönetici Seç</option>
                        {systemManagers.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 px-2.5 py-0.5 text-xs font-bold text-yellow-300">
                        {row.manager_username || row.created_by_username || "Atanmadı"}
                      </span>
                    )}
                  </div>

                  {/* 2. AJANS TANITIM GÖRÜŞMESİ */}
                  <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-black/40 px-3 py-1.5">
                    <span className="text-[11px] font-bold text-cyan-400">🤝 Tanıtım Görüşmesi:</span>
                    <select
                      value={row.intro_meeting_status || "beklemede"}
                      onChange={(e) => {
                        const nextStatus = e.target.value;
                        onInlineUpdate?.(row.id, { intro_meeting_status: nextStatus });
                        showToast?.(`${row.tiktok_username} tanıtım görüşme durumu güncellendi.`);
                      }}
                      className="rounded-lg bg-black/80 border border-white/15 px-2 py-0.5 text-xs font-bold text-white outline-none focus:border-cyan-400 [color-scheme:dark]"
                    >
                      {INTRO_MEETING_OPTIONS.map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 3. YAYINCI TARZI / KATEGORİSİ */}
                  <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-black/40 px-3 py-1.5">
                    <span className="text-[11px] font-bold text-purple-400">🎭 Yayıncı Tarzı:</span>
                    <select
                      value={row.publisher_style || "Sohbet & Muhabbet"}
                      onChange={(e) => {
                        const nextStyle = e.target.value;
                        onInlineUpdate?.(row.id, { publisher_style: nextStyle });
                        showToast?.(`${row.tiktok_username} tarzı '${nextStyle}' olarak kaydedildi.`);
                      }}
                      className="rounded-lg bg-black/80 border border-white/15 px-2 py-0.5 text-xs font-bold text-white outline-none focus:border-purple-400 [color-scheme:dark]"
                    >
                      {PUBLISHER_STYLES.map((st) => (
                        <option key={st.key} value={st.key}>
                          {st.icon} {st.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 4. İLETİŞİM / CRM BUTONU */}
                <div className="flex items-center gap-2">
                  <PublisherCrmBadgeButton
                    publisher={row}
                    onClick={() => setSelectedSlideOverPublisher(row)}
                  />
                </div>
              </div>

              {/* 4 GENEL GÖRÜŞME ALANI (TANIŞMA, İLK HAFTA, ELMAS PERFORMANS, MEZUNİYET) */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-wider text-white/50">
                    👑 Genel Görüşme Aşamaları ({row.completedCount}/4 Tamamlandı)
                  </span>
                  <span className="text-[10px] text-white/40">Detay girmek veya durumu güncellemek için aşamaya tıklayın</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {GENERAL_MEETING_STAGES.map((stage) => {
                    const stageData = getStageData(row.follow_up_stages, stage);
                    const isDone = Boolean(stageData.done);

                    return (
                      <button
                        key={stage.key}
                        type="button"
                        onClick={() => onOpenStageModal(row, stage.key)}
                        className={`group relative overflow-hidden rounded-2xl border p-3.5 text-left transition ${
                          isDone
                            ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent hover:border-emerald-500/60 shadow-lg shadow-emerald-500/5"
                            : "border-white/10 bg-white/[0.02] hover:border-yellow-500/40 hover:bg-white/[0.05]"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-0.5 text-[10px] font-black uppercase ${
                            isDone ? "border-emerald-500/40 bg-emerald-500/20 text-emerald-300" : stage.badgeColor
                          }`}>
                            <span>{stage.icon}</span>
                            <span>{stage.label}</span>
                          </span>
                          <span className={`text-xs font-black ${isDone ? "text-emerald-400" : "text-white/40"}`}>
                            {isDone ? "✓ Yapıldı" : "⏳ Bekliyor"}
                          </span>
                        </div>

                        <div className="mt-2 text-xs font-black text-white group-hover:text-yellow-300 transition truncate">
                          {stage.title}
                        </div>
                        <div className="text-[11px] text-white/50 truncate">{stage.desc}</div>

                        <div className="mt-2.5 pt-2 border-t border-white/5 flex items-center justify-between text-[10px]">
                          {isDone ? (
                            <>
                              <span className="text-emerald-300 font-bold truncate">👤 {stageData.by || "Yetkili"}</span>
                              <span className="text-white/40 font-mono">{formatDateTr(stageData.date)}</span>
                            </>
                          ) : (
                            <span className="text-white/40 italic">Görüşme notu girmek için tıkla</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ALTINDA EK GÖRÜŞME GİRİŞLERİ VE CRM AKIŞI */}
              <div className="rounded-2xl border border-white/10 bg-black/60 p-3.5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-yellow-400">💬 Ek Görüşme & İletişim Girişleri</span>
                    <span className="rounded-full bg-yellow-500/20 border border-yellow-500/40 px-2 py-0.2 text-[10px] font-black text-yellow-300">
                      {pubComms.length} Kayıt
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSlideOverPublisher(row)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-3 py-1.5 text-xs font-black text-black hover:brightness-110 active:scale-95 transition shadow-md"
                    >
                      <span>➕</span>
                      <span>Yeni Ek Görüşme Ekle</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectedSlideOverPublisher(row)}
                      className="inline-flex items-center gap-1 rounded-xl border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs font-bold text-white/70 hover:text-white hover:bg-white/10 transition"
                    >
                      <span>Tümünü Gör (Yan Panel)</span>
                      <span>›</span>
                    </button>
                  </div>
                </div>

                {pubComms.length > 0 ? (
                  <div className="space-y-2">
                    {pubComms.slice(0, 3).map((comm, cIdx) => (
                      <div
                        key={comm.id || cIdx}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-2.5 text-xs hover:border-white/15 transition"
                      >
                        <div className="flex items-start gap-2.5 min-w-0">
                          <span className="shrink-0 font-mono font-bold text-white/40 mt-0.5">#{pubComms.length - cIdx}</span>
                          <span className="shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-black border border-white/10 bg-white/5 text-white/80">
                            {comm.direction === "manager_to_publisher" ? "👑 Yönetici Ulaştı" : "📱 Yayıncı Ulaştı"}
                          </span>
                          <span className="shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-bold border border-yellow-500/30 bg-yellow-500/10 text-yellow-300">
                            {comm.category || "Genel"}
                          </span>
                          <p className="truncate text-white/80 font-medium">{comm.note}</p>
                        </div>

                        <div className="flex items-center gap-3 shrink-0 text-[11px] text-white/40 pl-6 sm:pl-0">
                          <span>👤 {comm.createdBy || "Yetkili"}</span>
                          <span>•</span>
                          <span className="font-mono">{formatDateTimeTr(comm.createdAt)}</span>
                          <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold border ${
                            comm.status === "Çözüldü"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                              : "border-amber-500/30 bg-amber-500/10 text-amber-300"
                          }`}>
                            {comm.status || "İşlemde"}
                          </span>
                        </div>
                      </div>
                    ))}

                    {pubComms.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setSelectedSlideOverPublisher(row)}
                        className="w-full py-1 text-center text-xs font-bold text-yellow-400/80 hover:text-yellow-300 transition"
                      >
                        + {pubComms.length - 3} diğer ek görüşmeyi yan panelde görüntüleyin ›
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-xl border border-dashed border-white/10 bg-white/[0.01] px-4 py-3 text-xs text-white/40">
                    <span>Bu yayıncı için henüz ek görüşme / talep kaydı girilmedi.</span>
                    <button
                      type="button"
                      onClick={() => setSelectedSlideOverPublisher(row)}
                      className="font-bold text-yellow-400 hover:underline"
                    >
                      + İlk Görüşmeyi Ekle
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {!filtered.length && (
          <div className="rounded-3xl border border-white/10 bg-black/40 p-12 text-center text-sm text-white/40">
            Seçili ayda (<b className="text-yellow-300">{currentMonthLabel}</b>) 15 günlük takip kriterine uygun yayıncı bulunmuyor.
          </div>
        )}
      </div>

      {/* YANDA AÇILIP KAPANAN SLIDE-OVER CRM MASASI */}
      <PublisherCrmSlideOver
        publisher={selectedSlideOverPublisher}
        currentUser={currentUser}
        isOpen={Boolean(selectedSlideOverPublisher)}
        onClose={() => setSelectedSlideOverPublisher(null)}
      />
    </div>
  );
}

/* ==========================================================================
   MODALLAR
   ========================================================================== */
function CandidateDetailModal({ row, currentUser, systemManagers = [], onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({ ...row });
  const isAdmin = checkIsAdmin(currentUser);

  function handleSubmit(e) {
    e.preventDefault();
    onSave(formData);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[2rem] border border-white/15 bg-[#0a0a0a] p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <h2 className="text-xl font-black text-white">Aday Bilgileri & Notlar</h2>
            <div className="text-xs text-white/50">{formData.tiktok_username}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 p-2 text-white/60 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">TikTok Kullanıcı Adı</span>
              <input
                type="text"
                value={formData.tiktok_username}
                onChange={(e) => setFormData({ ...formData, tiktok_username: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Ad Soyad</span>
              <input
                type="text"
                value={formData.full_name || ""}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Telefon No</span>
              <input
                type="text"
                value={formData.phone || ""}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+90 5XX XXX XX XX"
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400 font-mono"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Hidden ID</span>
              <input
                type="text"
                value={formData.hidden_id || ""}
                onChange={(e) => setFormData({ ...formData, hidden_id: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400 font-mono"
              />
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <div className="mb-1 flex items-center justify-between">
                <span className="block text-xs font-bold text-white/60">Bağlı / Sorumlu Yönetici</span>
                {!isAdmin && <span className="text-[10px] text-yellow-400/80 font-semibold">🔒 Sadece Admin</span>}
              </div>
              {isAdmin ? (
                <select
                  value={formData.manager_username || ""}
                  onChange={(e) => setFormData({ ...formData, manager_username: e.target.value })}
                  className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
                >
                  <option value="">Yönetici Seç</option>
                  {systemManagers.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <div className="flex h-10 items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3.5 text-xs font-bold text-yellow-300">
                  <span>👑 {formData.manager_username || row.created_by_username || "Atanmadı"}</span>
                  <span className="text-[10px] text-white/40">Kilitli</span>
                </div>
              )}
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Yayıncı Tarzı / Kategorisi</span>
              <select
                value={formData.publisher_style || "Sohbet & Muhabbet"}
                onChange={(e) => setFormData({ ...formData, publisher_style: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              >
                {PUBLISHER_STYLES.map((st) => (
                  <option key={st.key} value={st.key}>{st.icon} {st.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Ajans Tanıtım Görüşmesi</span>
              <select
                value={formData.intro_meeting_status || "beklemede"}
                onChange={(e) => setFormData({ ...formData, intro_meeting_status: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              >
                {INTRO_MEETING_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.icon} {opt.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Ajans Uygunluk Statüsü</span>
              <select
                value={formData.eligibility || "Uygun"}
                onChange={(e) => setFormData({ ...formData, eligibility: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              >
                <option value="Uygun">🟢 Uygun (Ajanssız)</option>
                <option value="Başka Ajans">🔴 Başka Bir Ajansta (Uygun Değil)</option>
                <option value="Uygun Değil">🟡 Kriter Dışı / İhlal</option>
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Ekipman Talebi</span>
              <select
                value={formData.equipment_request || "Talep Yok"}
                onChange={(e) => setFormData({ ...formData, equipment_request: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              >
                <option value="Talep Yok">Talep Yok</option>
                <option value="Talep Var">Talep Var</option>
                <option value="İnceleniyor">İnceleniyor</option>
                <option value="Gönderildi">Gönderildi</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Ek Bilgi (Şehir, Yaş vb.)</span>
              <input
                type="text"
                value={formData.extra_info || ""}
                onChange={(e) => setFormData({ ...formData, extra_info: e.target.value })}
                placeholder="Örn. İstanbul, 24 yaş, hafta içi müsait"
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-white/60">Görüşme ve Aday Notu</span>
            <textarea
              rows={3}
              value={formData.note || ""}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
            />
          </label>

          <div className="flex items-center justify-between border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={() => onDelete(formData.id)}
              className="flex items-center gap-1.5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs font-bold text-red-300 hover:bg-red-500 hover:text-white transition"
            >
              <span>🗑️</span>
              <span>Adayı Sil</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white/60 hover:text-white transition"
              >
                İptal
              </button>
              <button
                type="submit"
                className="rounded-xl bg-yellow-500 px-5 py-2.5 text-xs font-black text-black shadow-lg hover:bg-yellow-400 active:scale-95 transition"
              >
                Kaydet
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function StageFollowUpModal({ row, stageKey, currentUser, onClose, onSave }) {
  const stageDef = GENERAL_MEETING_STAGES.find((s) => s.key === stageKey || s.legacyKeys?.includes(stageKey)) || { label: "Aşama", title: "Görüşme Kaydı" };
  const currentStageData = getStageData(row.follow_up_stages || {}, stageDef);

  const [stageData, setStageData] = useState({
    done: Boolean(currentStageData.done),
    date: currentStageData.date || new Date().toISOString().slice(0, 10),
    by: currentStageData.by || currentUser?.fullName || currentUser?.username || "admin",
    note: currentStageData.note || "",
  });

  function handleSubmit(e) {
    e.preventDefault();
    onSave(stageKey, stageData);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-[2rem] border border-white/15 bg-[#0a0a0a] p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <div className="text-[10px] font-black uppercase text-yellow-400">{stageDef.label} - GENEL GÖRÜŞME</div>
            <h2 className="text-lg font-black text-white">{stageDef.title}</h2>
            <div className="text-xs text-white/50">{row.tiktok_username} ({row.full_name || "İsimsiz"})</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 p-2 text-white/60 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 cursor-pointer hover:bg-white/[0.06] transition">
            <input
              type="checkbox"
              checked={stageData.done}
              onChange={(e) => setStageData({ ...stageData, done: e.target.checked })}
              className="h-5 w-5 rounded border-white/20 bg-black/60 accent-emerald-400 cursor-pointer"
            />
            <div>
              <div className="text-xs font-black text-white">Bu Görüşme Yapıldı & Tamamlandı</div>
              <div className="text-[11px] text-white/50">Görüşme tamamlandı olarak işaretlenir ve ilerlemeye eklenir.</div>
            </div>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Görüşme Tarihi</span>
              <input
                type="date"
                value={stageData.date}
                onChange={(e) => setStageData({ ...stageData, date: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-bold text-white/60">Görüşen Yetkili / Yönetici</span>
              <input
                type="text"
                value={stageData.by}
                onChange={(e) => setStageData({ ...stageData, by: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-bold text-white/60">Görüşme Detayı & Değerlendirme Notları</span>
            <textarea
              rows={4}
              required
              value={stageData.note}
              onChange={(e) => setStageData({ ...stageData, note: e.target.value })}
              placeholder="Yayıncının performansı, hedefleri, yayın disiplini ve geri bildirimleri..."
              className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs text-white outline-none focus:border-yellow-400 placeholder:text-white/30"
            />
          </label>

          <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white/60 hover:text-white transition"
            >
              İptal
            </button>
            <button
              type="submit"
              className="rounded-xl bg-emerald-500 px-5 py-2.5 text-xs font-black text-black shadow-lg hover:bg-emerald-400 active:scale-95 transition"
            >
              Görüşmeyi Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GraduateManagerModal({ row, currentUser, systemManagers = [], onClose, onSave }) {
  const isAdmin = checkIsAdmin(currentUser);
  const defaultMgr = row.manager_username || row.created_by_username || currentUser?.fullName || currentUser?.username || (systemManagers.length ? systemManagers[0] : "admin");
  const [selectedManager, setSelectedManager] = useState(defaultMgr);

  function handleSubmit(e) {
    e.preventDefault();
    if (!selectedManager) return;
    onSave(selectedManager);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[2rem] border border-yellow-500/30 bg-[#0a0a0a] p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <div className="text-[10px] font-black uppercase text-yellow-400">15. GÜN MEZUNİYETİ</div>
            <h2 className="text-lg font-black text-white">Yayıncı Yöneticisi Ata & Mezun Et</h2>
            <div className="text-xs text-white/50">{row.tiktok_username}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 p-2 text-white/60 hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-4 text-xs text-yellow-200/90 leading-relaxed">
            Bu yayıncı 15 günlük oryantasyon sürecini tamamladı. Kalıcı yönetici havuzuna devredilerek mezun edilecektir.
          </div>

          <label className="block">
            <div className="mb-1 flex items-center justify-between">
              <span className="block text-xs font-bold text-white/60">Atanacak Yönetici</span>
              {!isAdmin && <span className="text-[10px] text-yellow-400 font-semibold">🔒 Sistem Yöneticisi Yetkisindedir</span>}
            </div>
            {isAdmin ? (
              <select
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-black/60 px-3.5 py-2.5 text-xs font-bold text-white outline-none focus:border-yellow-400 [color-scheme:dark]"
              >
                {systemManagers.length > 0 ? (
                  systemManagers.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="admin">admin (Yönetici)</option>
                    <option value="SenaBaran">Sena Baran (Yönetici)</option>
                    <option value="Zafer AKAN">Zafer AKAN (Yönetici)</option>
                  </>
                )}
              </select>
            ) : (
              <div className="rounded-xl border border-white/15 bg-white/5 px-3.5 py-2.5 text-xs font-bold text-yellow-300">
                👑 {selectedManager} (Sisteme Ekleyen Yönetici)
              </div>
            )}
          </label>

          <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white/60 hover:text-white transition"
            >
              İptal
            </button>
            <button
              type="submit"
              className="rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-5 py-2.5 text-xs font-black text-black shadow-lg hover:brightness-110 active:scale-95 transition"
            >
              Mezun Et & Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
